import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PaymentIcon from "@mui/icons-material/Payment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { AuthContext } from "./AuthContext";
import { useLocation } from "react-router-dom";
import { formatDateWithYear } from "../utils/hooks/dataHandler";

const SUBSCRIPTION_LOCK_GRACE_DAYS = 3;

const SubscriptionStatusContext = createContext(null);

export const useSubscriptionStatus = () =>
  useContext(SubscriptionStatusContext);

function getSubscriptionDeadlineInfo(subscription) {
  if (!subscription) {
    return {
      shouldWarn: false,
      isPastDue: false,
      isLocked: false,
      status: null,
      reasonDate: null,
      lockedDate: null,
      lockDate: null,
      planName: null,
      warningTitle: "",
      warningMessage: "",
      lockedMessage: "",
      daysRemaining: null,
    };
  }

  const status = String(subscription.status || "").toLowerCase();
  const isPastDue = subscription.isPastDue === true;
  const isLocked = subscription.isLocked === true;
  const shouldWarn = isPastDue || isLocked;
  const planName = subscription?.plan_name || null;

  if (!shouldWarn) {
    return {
      shouldWarn: false,
      isPastDue,
      isLocked: false,
      status,
      reasonDate: null,
      lockedDate: null,
      lockDate: null,
      planName,
      warningTitle: "",
      warningMessage: "",
      lockedMessage: "",
      daysRemaining: null,
    };
  }

  const reasonDate =
    status === "trialing"
      ? subscription.trial_end || null
      : subscription.next_billing_date || null;
  const lockedDate = subscription.lockedDate || null;

  const baseMessage =
    status === "trialing"
      ? "Your free trial has ended. Please create a subscription to keep using the app."
      : "Your subscription payment is due. Please update your subscription to keep using the app.";

  return {
    shouldWarn: true,
    isPastDue,
    isLocked,
    status,
    reasonDate,
    lockedDate,
    lockDate: lockedDate,
    planName,
    warningTitle:
      status === "trialing"
        ? "Trial Period Ended"
        : "Subscription Payment Overdue",
    warningMessage: `${baseMessage} If no subscription is made within ${SUBSCRIPTION_LOCK_GRACE_DAYS} days, access will be locked.`,
    lockedMessage:
      status === "trialing"
        ? "Your trial period has ended. A subscription is required before you can continue."
        : "Your subscription plan has ended because the subscription remains past due. A valid subscription is required before you can continue.",
    daysRemaining: null,
  };
}

export function SubscriptionStatusProvider({ children }) {
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const isSettingsPage = location.pathname === "/settings";
  const isInternalPage = location.pathname.includes("/internal");

  const { global_authState, authBootstrapComplete, refreshAuthenticatedUser } =
    authContext || {};
  const [dialogOpen, setDialogOpen] = useState(false);

  const subscriptionInfo = useMemo(() => {
    return getSubscriptionDeadlineInfo(global_authState?.subscription);
  }, [global_authState?.subscription]);

  /** Drop stale billing/warning state (e.g. after successful payment, before redirect). */
  const clearSubscriptionStatus = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const refreshSubscriptionStatus = useCallback(async () => {
    if (!refreshAuthenticatedUser) {
      return global_authState?.subscription || null;
    }
    const user = await refreshAuthenticatedUser();
    return user?.subscription || null;
  }, [global_authState?.subscription, refreshAuthenticatedUser]);

  useEffect(() => {
    if (isInternalPage) return;
    if (!authBootstrapComplete || !global_authState?.uid || isSettingsPage) {
      setDialogOpen(false);
      return;
    }

    setDialogOpen(subscriptionInfo.shouldWarn);
  }, [
    authBootstrapComplete,
    global_authState?.uid,
    global_authState?.subscription?.status,
    global_authState?.subscription?.isPastDue,
    global_authState?.subscription?.isLocked,
    global_authState?.subscription?.lockedDate,
    isInternalPage,
    isSettingsPage,
    subscriptionInfo.shouldWarn,
  ]);

  const handleClose = () => {
    if (subscriptionInfo.isLocked) return;
    setDialogOpen(false);
  };

  const handleGoToBilling = () => {
    window.location.href = "/settings?tab=billings";
  };

  return (
    <SubscriptionStatusContext.Provider
      value={{
        ...subscriptionInfo,
        graceDays: SUBSCRIPTION_LOCK_GRACE_DAYS,
        dialogOpen,
        setDialogOpen,
        clearSubscriptionStatus,
        refreshSubscriptionStatus,
      }}
    >
      {children}
      <SubscriptionStatusDialog
        open={dialogOpen}
        onClose={handleClose}
        onGoToBilling={handleGoToBilling}
        subscriptionInfo={subscriptionInfo}
      />
    </SubscriptionStatusContext.Provider>
  );
}

function SubscriptionStatusDialog({
  open,
  onClose,
  onGoToBilling,
  subscriptionInfo,
}) {
  const theme = useTheme();
  const {
    isLocked,
    reasonDate,
    lockDate,
    planName,
    warningTitle,
    warningMessage,
    lockedMessage,
    daysRemaining,
  } = subscriptionInfo;

  const palette = isLocked ? theme.palette.error : theme.palette.warning;
  const title = isLocked ? "Subscription Required" : warningTitle;
  const description = isLocked ? lockedMessage : warningMessage;

  return (
    <Dialog
      open={open}
      onClose={isLocked ? undefined : onClose}
      disableEscapeKeyDown={isLocked}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: alpha(palette.main, 0.28),
          boxShadow: `0 12px 36px ${alpha(palette.dark, 0.28)}`,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.common.black, 0.6),
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: { xs: 2.5, sm: 3 },
            background: alpha(palette.main, 0.12),
            borderBottom: "1px solid",
            borderColor: alpha(palette.main, 0.22),
          }}
        >
          <Stack direction='row' spacing={1.75} alignItems='center'>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "14px",
                background: `linear-gradient(135deg, ${palette.dark} 0%, ${palette.main} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: palette.contrastText,
                flexShrink: 0,
                boxShadow: `0 6px 18px ${alpha(palette.dark, 0.35)}`,
              }}
            >
              {isLocked ? (
                <LockOutlinedIcon sx={{ fontSize: 24 }} />
              ) : (
                <WarningAmberRoundedIcon sx={{ fontSize: 24 }} />
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.25,
                  color: palette.dark,
                }}
              >
                {title}
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  mt: 0.5,
                  color: alpha(palette.dark, 0.82),
                }}
              >
                Billing action is needed to continue using Tax Savy.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          bgcolor: alpha(palette.main, 0.03),
          pt: 3,
        }}
      >
        <Stack spacing={2.25} sx={{ mt: 2 }}>
          <Typography
            variant='body1'
            sx={{
              lineHeight: 1.75,
              color: "text.primary",
            }}
          >
            {description}
          </Typography>

          <Box
            sx={{
              p: 2.25,
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha(palette.main, 0.18),
              backgroundColor: alpha(theme.palette.common.white, 0.92),
            }}
          >
            <Stack spacing={1.25}>
              {planName ? (
                <Typography variant='body2' sx={{ color: "text.secondary" }}>
                  Current plan:{" "}
                  <Typography
                    component='span'
                    fontWeight={700}
                    color='text.primary'
                  >
                    {planName}
                  </Typography>
                </Typography>
              ) : null}

              <Stack direction='row' spacing={1.25} alignItems='center'>
                <AccessTimeIcon sx={{ color: palette.dark, fontSize: 20 }} />
                <Typography variant='body2' sx={{ color: "text.secondary" }}>
                  Expired on:{" "}
                  <Typography
                    component='span'
                    fontWeight={700}
                    color='text.primary'
                  >
                    {formatDateWithYear(reasonDate)}
                  </Typography>
                </Typography>
              </Stack>

              {lockDate ? (
                <Stack direction='row' spacing={1.25} alignItems='center'>
                  <LockOutlinedIcon
                    sx={{ color: palette.dark, fontSize: 20 }}
                  />
                  <Typography variant='body2' sx={{ color: "text.secondary" }}>
                    Lock date:{" "}
                    <Typography
                      component='span'
                      fontWeight={700}
                      color='text.primary'
                    >
                      {formatDateWithYear(lockDate)}
                    </Typography>
                  </Typography>
                </Stack>
              ) : null}

              {!isLocked && daysRemaining != null ? (
                <Typography
                  variant='body2'
                  sx={{ color: palette.dark, fontWeight: 700 }}
                >
                  {daysRemaining <= 1
                    ? "Less than 1 day remaining before access is locked."
                    : `${daysRemaining} days remaining before access is locked.`}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2.5,
          gap: 1.25,
          justifyContent: "flex-end",
          background: alpha(palette.main, 0.08),
          borderTop: "1px solid",
          borderColor: alpha(palette.main, 0.18),
        }}
      >
        {!isLocked ? (
          <Button
            onClick={onClose}
            variant='text'
            sx={{ fontWeight: 600, color: "warning.dark" }}
          >
            Continue for now
          </Button>
        ) : null}

        <Button
          onClick={onGoToBilling}
          variant='contained'
          startIcon={<PaymentIcon />}
          sx={{
            fontWeight: 700,
            bgcolor: isLocked ? "error.main" : "warning.main",
            "&:hover": {
              bgcolor: isLocked ? "error.dark" : "warning.dark",
            },
          }}
        >
          Go to Billing
        </Button>
      </DialogActions>
    </Dialog>
  );
}
